import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, MenuItem } from '@mui/material';
import { Box } from '@mui/system';
import { Text18 } from '../../../ui/Text18';
import { InsuranceContext } from '../../../context/insuranceContext';

import {
  CountryType,
  InsuranceParameters,
  SetInsuranceParameters,
} from '../../../types';
import useGetCountries from '../../../hooks/useGetCountries';
import { 
  InputWrapper, 
  InputTitle, 
  FormCountry, 
  PopularCountries, 
  PopularCountryText,
  SelectWrapper,
  Label 
} from '../style';

type Props = {
  insuranceParameters?: InsuranceParameters;
  setInsuranceParameters?: SetInsuranceParameters;
};

const CountrySelect: React.FC<Props> = ({
  insuranceParameters,
  setInsuranceParameters,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  
  const { modalsVisibility, selectedCountry } = insuranceParameters;
  const { countries } = useGetCountries();

  const availableCountries = countries?.popular.concat(countries?.other);
  const popularCountries = countries?.popular;

  const { insurance, setInsurance } = useContext(InsuranceContext);

  const handleDelete = () => {   
    setInsuranceParameters({
      ...insuranceParameters,
      selectedCountry: {
        ...insuranceParameters.selectedCountry,       
        id: '',
      },
    });
  };

  const handleChange = (event: any) => {
    const { id, name } = event?.target?.value
      ? JSON.parse(event.target.value)
      : JSON.parse(event);    

    setInsurance({
      ...insurance,
      details: {
        ...insurance.details,
        destination: id,
        destination_name: name[language.slice(0, 2)]
      }
    });

    setInsuranceParameters({
      ...insuranceParameters,
      selectedCountry: {
        name,
        id,
      },
    });     
  };

  return (
    <InputWrapper>
      <InputTitle>{t('where_to_go')}</InputTitle>
      <FormCountry fullWidth>
        <Label id='select-country'>{t('select_country')}</Label>
        <SelectWrapper
          inputProps={{
            id: selectedCountry.id,
            name: selectedCountry.name[language],
          }}
          labelId='select-country'
          id='select-country'
          value={
            selectedCountry.id
              ? JSON.stringify({
                  id: selectedCountry.id,
                  name: selectedCountry.name,
                })
              : insurance.details.destination ? 
                JSON.stringify({
                    id: insurance.details.destination,
                    name: insurance.details.destination_name
                }) : ''
          }
          label='Select country'
          onChange={handleChange}
          renderValue={(selected: string) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                key={JSON.parse(selected).name[language.slice(0, 2)] || insurance.details.destination_name}
                label={JSON.parse(selected).name[language.slice(0, 2)] || insurance.details.destination_name}
                onMouseDown={e => e.stopPropagation()}
                onDelete={handleDelete}
              />             
            </Box>
          )}
        >
          {availableCountries?.map((country: CountryType, index: number) => {
            return (
              <MenuItem
                key={country.ccn3 + index}
                id={country.ccn3}
                value={JSON.stringify({
                  id: country.ccn3,
                  name: country.language,
                })}
              >
                <Text18>{country.language[language.slice(0, 2)]}</Text18>
              </MenuItem>
            );
          })}
        </SelectWrapper>
      </FormCountry>
      <PopularCountries>
        {popularCountries?.map((country: CountryType, index: number) => {
          return (
            <PopularCountryText
              key={country.ccn3}
              onClick={() =>
                handleChange(
                  JSON.stringify({
                    id: country.ccn3,
                    name: country.language,
                  })
                )
              }
            >
              {country.name === 'EU'
                ? country.name + ','
                : country.language[language.slice(0, 2)] + ','}
            </PopularCountryText>
          );
        })}
      </PopularCountries>
      {modalsVisibility.countries && (
        <CountrySelect
          insuranceParameters={insuranceParameters}
          setInsuranceParameters={setInsuranceParameters}
        />
      )}
    </InputWrapper>
  );
};

export default CountrySelect;
